export type EnumOption<V extends string = string> = {
  value: V;
  label: string;
};

export const HTTP_METHODS = [
  { value: 'ANY', label: 'Any' },
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'HEAD', label: 'HEAD' },
  { value: 'OPTIONS', label: 'OPTIONS' },
] as const satisfies readonly EnumOption[];

export type HttpMethod = (typeof HTTP_METHODS)[number]['value'];

export const TRY_IT_METHODS = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'HEAD',
  'OPTIONS',
] as const;

export const METHODS_WITH_BODY = ['POST', 'PUT', 'PATCH', 'DELETE'];

export const AUTH_MODES = [
  { value: 'none', label: 'None (open)' },
  { value: 'api_key', label: 'API key header' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'basic', label: 'HTTP Basic' },
] as const satisfies readonly EnumOption[];

export type MockAuthMode = (typeof AUTH_MODES)[number]['value'];

export const RESPONSE_MODES = [
  { value: 'rules', label: 'Rules, then default' },
  { value: 'sequential', label: 'Sequential (cycle)' },
  { value: 'random', label: 'Random' },
] as const satisfies readonly EnumOption[];

export type MockResponseMode = (typeof RESPONSE_MODES)[number]['value'];

export const CONDITION_MODES = [
  { value: 'all', label: 'All conditions (AND)' },
  { value: 'any', label: 'Any condition (OR)' },
] as const satisfies readonly EnumOption[];

export type MockConditionMode = (typeof CONDITION_MODES)[number]['value'];

export const REQUEST_OUTCOMES = [
  { value: 'matched', label: 'Matched' },
  { value: 'no_route', label: 'No route (404)' },
  { value: 'bad_method', label: 'Method not allowed (405)' },
  { value: 'unauthorised', label: 'Auth failed (401)' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'error', label: 'Error' },
] as const satisfies readonly EnumOption[];

export type MockRequestOutcome = (typeof REQUEST_OUTCOMES)[number]['value'];

export const CONDITION_SOURCES = [
  { value: 'path', label: 'Path param' },
  { value: 'query', label: 'Query param' },
  { value: 'header', label: 'Header' },
  { value: 'cookie', label: 'Cookie' },
  { value: 'body', label: 'Body' },
  { value: 'method', label: 'Method' },
] as const satisfies readonly EnumOption[];

export type MockConditionSource = (typeof CONDITION_SOURCES)[number]['value'];

export const CONDITION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'regex', label: 'matches regex' },
  { value: 'exists', label: 'exists' },
  { value: 'absent', label: 'is absent' },
  { value: 'in', label: 'is one of' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
] as const satisfies readonly EnumOption[];

export type MockConditionOperator =
  (typeof CONDITION_OPERATORS)[number]['value'];

export const CONTENT_TYPES = [
  'application/json',
  'application/problem+json',
  'application/hal+json',
  'application/vnd.api+json',
  'application/xml',
  'text/xml',
  'text/plain',
  'text/csv',
  'application/x-www-form-urlencoded',
  'application/octet-stream',
] as const;

export const DEFAULT_AUTH_HEADER_NAME = 'X-API-Key';

export const MOCK_API_MAX_DELAY_MS = 5000;

export const enumLabel = (
  options: readonly EnumOption[],
  value: string | null | undefined,
) => options.find((option) => option.value === value)?.label ?? value ?? '';
